import RightArrow from "@/components/icons/right-arrow-icon";
import { cn } from "@/libs/base/utils/layout";
import { cva } from "class-variance-authority";
import Link from "next/link";
import { HTMLAttributes, forwardRef } from "react";

type ButtonVariant = "Outline" | "Filled";
type ButtonBackground = "Black" | "Grey";
type ButtonShape = "Rectangle" | "Rounded";
type ButtonSize = "Normal" | "Small";

interface ButtonProps {
  text: string;
  href?: string;
  type?: HTMLButtonElement["type"];
  disabled?: boolean;
  openInNewTab?: boolean;

  hasArrow?: boolean;
  shape?: ButtonShape;
  background?: ButtonBackground;
  variant?: ButtonVariant;
  size?: ButtonSize;
  className?: string;
  // From builder.io
  attributes?: any;
}

type Variant<T extends string | boolean> = Record<T extends boolean ? "true" | "false" : T, string>;

const button = cva("flex text-xs items-center transition-colors border select-none", {
  variants: {
    hasArrow: {
      true: "justify-between",
      false: "justify-center",
    } satisfies Variant<boolean>,
    shape: {
      Rectangle: "",
      Rounded: "rounded-full",
    } satisfies Variant<ButtonShape>,
    variant: {
      Outline: "",
      Filled: "",
    } satisfies Variant<ButtonVariant>,
    background: {
      Black: "",
      Grey: "",
    } satisfies Variant<ButtonBackground>,
    size: {
      Normal: "gap-7.5 py-2.5",
      Small: "gap-2.5 py-2",
    } satisfies Variant<ButtonSize>,
  },
  compoundVariants: [
    {
      hasArrow: true,
      size: "Normal",
      className: "px-4",
    },
    {
      hasArrow: false,
      size: "Normal",
      className: "px-7.5",
    },
    // TODO: This isn't defined
    {
      hasArrow: true,
      size: "Small",
      className: "px-2.5",
    },
    {
      hasArrow: false,
      size: "Small",
      className: "px-4.5",
    },
    {
      background: "Black",
      variant: "Filled",
      className:
        "border-black bg-black text-white hover:bg-white hover:text-black active:border-transparent active:bg-lightgray-300/40 active:text-black",
    },
    {
      background: "Black",
      variant: "Outline",
      className:
        "border-black bg-lightgray-300/40 text-black hover:bg-black hover:text-white active:bg-white active:text-black",
    },
    {
      background: "Grey",
      variant: "Filled",
      className:
        "border-lightgray-100 bg-lightgray-100 text-black hover:border-white hover:bg-black hover:text-white active:border-transparent active:bg-lightgray-300/40 active:text-white",
    },
    {
      background: "Grey",
      variant: "Outline",
      className:
        "border-white bg-lightgray-300/40 text-white hover:border-lightgray-100 hover:bg-lightgray-100 hover:text-black active:border-black active:bg-black active:text-white",
    },
  ],
});

export const Button = forwardRef<
  HTMLAnchorElement | HTMLButtonElement,
  HTMLAttributes<HTMLAnchorElement | HTMLButtonElement> & ButtonProps
>(
  (
    {
      type = "button",
      text,
      variant = "Filled",
      background = "Black",
      shape = "Rectangle",
      hasArrow = false,
      openInNewTab = false,
      size = "Normal",
      className = "",
      attributes = {},
      href,
      disabled,
      onClick,
      ...props
    },
    ref,
  ) => {
    const Element = href !== undefined ? (href.startsWith("http") ? "a" : Link) : "button";

    return (
      <Element
        {...props}
        {...attributes}
        type={type}
        ref={ref}
        className={cn(button({ variant, background, shape, hasArrow, size }), className, attributes?.className)}
        href={disabled ? "" : href}
        target={openInNewTab ? "_blank" : undefined}
        disabled={disabled}
        onClick={(e) => {
          if (disabled) {
            e.preventDefault();
            e.stopPropagation();
            return;
          }

          onClick?.(e);
        }}
      >
        <span>{text}</span>

        {hasArrow && <RightArrow className="h-4 w-4" />}
      </Element>
    );
  },
);
